import { createEffect, onCleanup, untrack } from "solid-js"
import { createStore, reconcile } from "solid-js/store"
import {
  getActiveIntegrationSync, getIntegrationSync, listFileIntegrations, retryIntegrationSync, syncIntegrationFiles,
  type DatoxFileIntegration, type DatoxFileSync,
} from "./api"

export type FileIntegrationScope = { projectID: string; periodID: string; periodName: string }

export function fileSyncActive(job?: DatoxFileSync) {
  return !!job && ["queued", "discovering", "syncing"].includes(job.status)
}

export function fileSyncFailed(job?: DatoxFileSync) {
  return !!job && ["failed", "partially_failed"].includes(job.status)
}

export function fileSyncErrorKey(code?: string | null) {
  if (code === "period_date_required") return "datox.fileIntegrations.periodDate"
  if (code === "period_changed") return "datox.fileIntegrations.periodChanged"
  if (code === "period_unavailable") return "datox.fileIntegrations.periodUnavailable"
  if (code === "connection_unavailable" || code === "source_unavailable") return "datox.fileIntegrations.connectionUnavailable"
  if (code === "connection_changed") return "datox.fileIntegrations.connectionChanged"
  if (code === "replacement_required") return "datox.fileIntegrations.replacementRequired"
  if (code === "too_many_files") return "datox.fileIntegrations.tooManyFiles"
  if (code === "file_too_large") return "datox.fileIntegrations.fileTooLarge"
  if (code === "file_sync_in_progress") return "datox.fileIntegrations.inProgress"
  if (code === "file_sync_needs_attention") return "datox.fileIntegrations.needsAttention"
  return "datox.fileIntegrations.failed"
}

function errorCode(error: unknown) {
  if (!(error instanceof Error)) return "failed"
  try {
    const body: unknown = JSON.parse(error.message)
    if (body && typeof body === "object" && "detail" in body && typeof body.detail === "string") return body.detail
  } catch { /* Non-JSON network errors use the translated fallback. */ }
  return "failed"
}

export function useFileIntegrations(props: { scope: () => FileIntegrationScope | undefined; onFiles: () => void; onComplete?: (job: DatoxFileSync, scope: FileIntegrationScope) => void }) {
  const [state, setState] = createStore({ connections: [] as DatoxFileIntegration[], jobs: {} as Record<string, DatoxFileSync>, loading: false,
    loadError: false, requestError: "", busyID: "" })
  let generation = 0
  let timer: ReturnType<typeof setTimeout> | undefined
  let disposed = false
  let requestIDs: Record<string, string> = {}
  const current = (version: number) => !disposed && generation === version
  const stop = () => { clearTimeout(timer); timer = undefined }
  const accept = (scope: FileIntegrationScope, version: number, job: DatoxFileSync | null) => {
    if (!current(version) || !job || job.project_id !== scope.projectID || job.period_id !== scope.periodID) return
    const previous = state.jobs[job.integration_id]
    if (previous?.id === job.id && previous.updated_at > job.updated_at) return
    const changed = job.imported !== previous?.imported || job.status !== previous?.status && !fileSyncActive(job)
    const finished = fileSyncActive(previous) && !fileSyncActive(job)
    setState("jobs", job.integration_id, reconcile(job))
    if (changed) props.onFiles()
    if (finished) props.onComplete?.(job, scope)
  }
  const schedule = (scope: FileIntegrationScope, version: number) => {
    stop()
    if (!current(version) || !Object.values(state.jobs).some(fileSyncActive)) return
    timer = setTimeout(() => void poll(scope, version), 5000)
  }
  const poll = async (scope: FileIntegrationScope, version: number) => {
    if (!current(version)) return
    try {
      const jobs = await Promise.all(Object.values(state.jobs).filter(fileSyncActive).map((job) => getIntegrationSync(scope.projectID, scope.periodID, job.id)))
      if (!current(version)) return
      for (const job of jobs) accept(scope, version, job)
      setState("loadError", false)
    } catch {
      if (current(version)) setState("loadError", true)
    } finally {
      if (current(version)) schedule(scope, version)
    }
  }
  const load = async () => {
    const scope = props.scope()
    const version = generation
    if (!scope || disposed || state.loading || state.busyID) return
    setState({ loading: true, loadError: false })
    stop()
    try {
      const connections = await listFileIntegrations(scope.projectID)
      if (!current(version)) return
      setState("connections", reconcile(connections))
      const jobs = await Promise.all(connections.map((connection) => getActiveIntegrationSync(scope.projectID, scope.periodID, connection.id)))
      if (!current(version)) return
      for (const job of jobs) accept(scope, version, job)
    } catch {
      if (current(version)) setState("loadError", true)
    } finally {
      if (current(version)) { setState("loading", false); schedule(scope, version) }
    }
  }
  createEffect(() => {
    const scope = props.scope()
    scope?.projectID
    scope?.periodID
    generation += 1
    stop()
    requestIDs = {}
    setState({ connections: [], loading: false, loadError: false, requestError: "", busyID: "" })
    setState("jobs", reconcile({}))
    if (scope) untrack(() => void load())
  })
  onCleanup(() => { disposed = true; generation += 1; stop() })

  const sync = async (integrationID: string) => {
    const scope = props.scope()
    const version = generation
    if (!scope || disposed || state.loading || state.busyID || state.loadError || fileSyncActive(state.jobs[integrationID])) return
    const job = state.jobs[integrationID]
    const requestID = requestIDs[integrationID] ?? crypto.randomUUID()
    requestIDs[integrationID] = requestID
    setState({ busyID: integrationID, requestError: "" })
    try {
      const result = fileSyncFailed(job)
        ? await retryIntegrationSync(scope.projectID, scope.periodID, job.id)
        : await syncIntegrationFiles(scope.projectID, scope.periodID, integrationID, requestID)
      if (!current(version)) return
      delete requestIDs[integrationID]
      accept(scope, version, result)
    } catch (error) {
      if (current(version)) setState("requestError", errorCode(error))
    } finally {
      if (current(version)) { setState("busyID", ""); schedule(scope, version) }
    }
  }
  return { state, load, sync, active: () => !!state.busyID || Object.values(state.jobs).some(fileSyncActive),
    blocked: () => !!state.busyID || Object.values(state.jobs).some((job) => fileSyncActive(job) || fileSyncFailed(job)) }
}
